import { features } from 'constants/featureFlags';
import { TFunction } from 'i18next';
import { Space } from 'interfaces/space';
import { Controller, useForm } from 'react-hook-form';
import { PostBody } from 'types/post';

import React, { useEffect, useRef } from 'react';

import { Select, Text } from '@thinkific/chalk';

import { zodResolver } from '@hookform/resolvers/zod';

import { useCommunityContext } from 'components/CommunityContext';
import { getErrorMap } from 'components/Forms/utils/defaultErrorMap';
import RichTextEditor, { RichTextEditorHandle } from 'components/RichTextEditor/RichTextEditor';
import UserAvatar from 'components/UserAvatar';
import { DraftPost, useGlobalState } from 'components/new/providers/GlobalStateProvider';

import { getSchema, getSchemaOld } from 'schema/Post';
import { Attachment } from 'schema/Post/types';

import * as css from './PostForm.css';

interface PostFormProps {
  initialValues?: {
    title: string;
    content: string;
    body: PostBody | null;
    spaceId: string;
    attachments: Attachment[];
    mentionedUserIds: string[];
  };
  isEdit?: boolean;
  cancelEdit?: () => void;
  onSubmit: (
    data: FormValues,
    { reset, editorRef }: { reset: () => void; editorRef: React.RefObject<RichTextEditorHandle> }
  ) => Promise<void>;
  isSubmitting: boolean;
  spacesList: { value: string; label: string; dangerousFontFamily?: string }[];
  selectedSpace: Space;
  showSpaceSelect?: boolean;
  t: TFunction;
}

export interface FormValues {
  title: string;
  content: string;
  body: PostBody;
  spaceId: string;
  attachments: Attachment[];
  mentionedUserIds: string[];
}

const PostForm = ({
  initialValues,
  isEdit = false,
  cancelEdit,
  onSubmit,
  isSubmitting,
  spacesList,
  selectedSpace,
  showSpaceSelect = false,
  t,
}: PostFormProps) => {
  const { currentUser, featureFlags } = useCommunityContext();
  const tiptapEditorEnabled = featureFlags[features.TIPTAP_EDITOR];
  const editorRef = useRef<RichTextEditorHandle>(null);
  const mentionsTextAreaRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const { createOrUpdateDraftPost, getDraftPost, removeDraftPost } = useGlobalState();

  // Get user avatar info
  const { id: userId, avatarUrl, fullName } = currentUser;
  const srcSet = `${avatarUrl}?width=40 1x, ${avatarUrl}?width=40&dpr=2 2x, ${avatarUrl}?width=40&dpr=3 3x`;

  const defaultValues = initialValues ||
    (selectedSpace && getDraftPost(selectedSpace.id)) || {
      title: '',
      content: '',
      body: {
        jsonContent: null,
        plainText: '',
      },
      spaceId: selectedSpace?.id || spacesList[0]?.value || '',
      attachments: [],
      mentionedUserIds: [],
    };

  const {
    control,
    handleSubmit,
    formState: { errors },
    getValues,
    setValue,
    watch,
    reset,
  } = useForm({
    defaultValues,
    resolver: zodResolver(tiptapEditorEnabled ? getSchema(t) : getSchemaOld(t), {
      errorMap: getErrorMap(t),
    }),
  });

  useEffect(() => {
    // If the spaceId is not set, set it to the first space in the list
    const spaceId = getValues('spaceId');
    if (!spaceId) {
      setValue('spaceId', selectedSpace?.id || spacesList[0]?.value);
    }
  }, [spacesList]);

  const body = watch('body');
  const attachments = watch('attachments');

  useEffect(() => {
    if (isEdit || !tiptapEditorEnabled) return;
    const formValues = watch();
    createOrUpdateDraftPost({
      ...formValues,
      attachments: attachments.filter((attachment) => !attachment.isLoading),
      draftId: selectedSpace?.id || formValues.spaceId,
    } as DraftPost);
  }, [body, attachments, selectedSpace, isEdit]);

  useEffect(() => {
    if (!tiptapEditorEnabled) return;
    if (selectedSpace && !isEdit) {
      reset(getDraftPost(selectedSpace.id));
    }
  }, [selectedSpace]);

  const handleFormSubmit = async (data) => {
    if (tiptapEditorEnabled) {
      removeDraftPost(data.spaceId);
    }
    await onSubmit(data, { reset, editorRef });
  };

  return (
    <div className={css.formWrapper} data-qa="post-form">
      <UserAvatar
        avatarUrl={avatarUrl}
        size="sm"
        srcSet={srcSet}
        userId={userId}
        username={fullName || ''}
        shouldOpenProfile
      />
      <div className={css.formLabel}>
        <div className={css.spaceSelectContainer}>
          <Text variant="help-text.md">
            {isEdit
              ? t('components-postForm-editing_post_in', 'Editing post in')
              : t('components-postForm-create_new_post_in', 'Create new post in')}
          </Text>
          {!showSpaceSelect ? (
            <Text variant="help-text.md">{selectedSpace?.name}</Text>
          ) : (
            <Controller
              control={control}
              name="spaceId"
              render={({ field }) => (
                <Select
                  a11yLabel={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                  className={css.spaceSelect}
                  dataQa="post-form__space-select"
                  defaultValue={spacesList[0]?.value}
                  label={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                  options={spacesList}
                  placeholder={t('components-forms-post-postForm-select_a_space', 'Select a space')}
                  value={field.value}
                  hiddenLabel
                  required
                  onChange={field.onChange}
                />
              )}
            />
          )}
        </div>
      </div>

      <div className={css.editorWrapper}>
        <RichTextEditor
          cancelEdit={cancelEdit}
          // @ts-ignore
          control={control}
          errors={errors}
          isEditing={isEdit}
          isSubmitting={isSubmitting}
          mentionsTextAreaRef={mentionsTextAreaRef}
          ref={editorRef}
          setValue={setValue}
          t={t}
          showTitle
          onSubmit={handleSubmit(handleFormSubmit)}
        />
      </div>
    </div>
  );
};

export default PostForm;
